import {
  AfterViewInit, ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UIBaseModal, UIModal } from '../../../../modal';
import { UIButton } from '../../../../button';
import { TimePickerValue } from '../../../models';
import { NgForOf } from '@angular/common';
import { LocalizationPipe } from '@shared/modules/localization';

@Component({
  selector: 'ui-form-timepicker-modal',
  templateUrl: './timepicker-modal.component.html',
  styleUrl: './timepicker-modal.component.scss',
  imports: [UIModal, UIButton, NgForOf, LocalizationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UITimepickerModalComponent
  extends UIBaseModal<TimePickerValue>
  implements OnInit, AfterViewInit
{
  @ViewChild('hourItemsCont') hourItemsCont?: ElementRef;
  @ViewChild('minuteItemsCont') minuteItemsCont?: ElementRef;
  @ViewChildren('hourRefs') private _hourRefs?: QueryList<ElementRef>;
  @ViewChildren('minuteRefs') private _minuteRefs?: QueryList<ElementRef>;

  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);

  hourValue?: number;
  minuteValue?: number;

  private hourListener?: () => void;
  private minuteListener?: () => void;

  // Configuration for scrolling
  ARRAY_LENGTH = 10;
  containerHeight = 250;
  itemHeight = 50;

  constructor() {
    super();
  }

  hourItems: Array<{ value: number; label: string }> = Array.from(
    { length: this.ARRAY_LENGTH },
    () =>
      Array.from({ length: 24 }, (_, index) => ({
        value: index,
        label: `${index.toString().padStart(2, '0')}`,
      })),
  ).flat();

  minuteItems: Array<{ value: number; label: string }> = Array.from(
    { length: this.ARRAY_LENGTH },
    () =>
      Array.from({ length: 60 }, (_, index) => ({
        value: index,
        label: `${index.toString().padStart(2, '0')}`,
      })),
  ).flat();

  get hourRefs(): HTMLElement[] {
    return this._hourRefs ? this._hourRefs.toArray().map(item => item.nativeElement) : [];
  }

  get minuteRefs(): HTMLElement[] {
    return this._minuteRefs ? this._minuteRefs.toArray().map(item => item.nativeElement) : [];
  }

  ngOnInit(): void {
    if (this.data?.hour != null && this.data?.minute != null) {
      this.hourValue = this.data?.hour;
      this.minuteValue = this.data?.minute;
    } else {
      const now = new Date();
      this.hourValue = now.getHours();
      this.minuteValue = now.getMinutes();
    }
  }

  ngAfterViewInit(): void {
    this.cdr.markForCheck();

    setTimeout(() => {
      this.initializeScrollEventListeners();
      this.scrollToCurrent();
    }, 0);
  }

  private initializeScrollEventListeners(): void {
    if (!this.hourItemsCont?.nativeElement || !this.minuteItemsCont?.nativeElement) {
      return;
    }

    this.hourListener = this.renderer.listen(this.hourItemsCont.nativeElement, 'scroll', event =>
      this.handleScroll(event, this.hourRefs, this.hourItems, 'hour'),
    );

    this.minuteListener = this.renderer.listen(
      this.minuteItemsCont.nativeElement,
      'scroll',
      event => this.handleScroll(event, this.minuteRefs, this.minuteItems, 'minute'),
    );
  }

  private handleScroll(
    event: any,
    refs: HTMLElement[],
    items: Array<{ value: number; label: string }>,
    type: 'hour' | 'minute',
  ): void {
    const scrollTop = Math.max(event.target.scrollTop, 0);
    const selectedElement = Math.min(
      Math.max(Math.floor(scrollTop / this.itemHeight), 0),
      items.length - 1,
    );

    setTimeout(() => {
      if (type === 'hour') {
        this.hourValue = items[selectedElement].value;
      } else if (type === 'minute') {
        this.minuteValue = items[selectedElement].value;
      }
      this.cdr.markForCheck();
    }, 0);
  }

  private scrollToCurrent(): void {
    this.scrollToCurrentValue(
      this.hourItems,
      this.hourValue!,
      this.hourItemsCont?.nativeElement,
      this.hourRefs,
    );
    this.scrollToCurrentValue(
      this.minuteItems,
      this.minuteValue!,
      this.minuteItemsCont?.nativeElement,
      this.minuteRefs,
    );
  }

  private scrollToCurrentValue(
    items: Array<{ value: number; label: string }>,
    value: number,
    container: HTMLElement,
    refs: HTMLElement[],
  ): void {
    if (!container || !refs.length) {
      return;
    }

    const allIndices = items
      .map((item, index) => (item.value === value ? index : -1))
      .filter(index => index !== -1);

    if (allIndices.length === 0) {
      return;
    }

    const middleIndex = allIndices[Math.floor(allIndices.length / 2)];

    if (refs && refs[middleIndex]) {
      const scrollTop = middleIndex * this.itemHeight;
      container.scrollTo({
        top: scrollTop,
        behavior: 'auto',
      });
    }
  }

  submit(): void {
    if (this.hourValue != null && this.minuteValue != null) {
      const result: TimePickerValue = {
        hour: this.hourValue,
        minute: this.minuteValue,
      };
      this.onAfterClose(result);
      this.cleanupListeners();
    }
  }

  private cleanupListeners(): void {
    if (this.hourListener) {
      this.hourListener();
      this.hourListener = undefined;
    }
    if (this.minuteListener) {
      this.minuteListener();
      this.minuteListener = undefined;
    }
  }
}
